import React from 'react';
import { motion } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiAlertTriangle, FiMapPin, FiThermometer, FiWind, FiDroplet } = FiIcons;

const MindanaoSevereWeather = ({ data, typhoonData }) => {
  // Generate severe weather alerts for Mindanao cities
  const generateMindanaoSevereWeatherAlerts = () => {
    const cities = [
      {
        name: 'Iligan City',
        region: 'Northern Mindanao',
        severity: typhoonData ? 'HIGH' : 'MEDIUM',
        warnings: typhoonData ? [
          'Mandulog River critical flood level',
          'Coastal storm surge up to 2-3m',
          'Landslide risk in highland barangays'
        ] : [
          'Mandulog River monitoring active',
          'Flash flood watch for Tubod area',
          'Creek overflow possible in Saray'
        ],
        windSpeed: typhoonData ? '85-120 kph' : '25-45 kph',
        temperature: '24-28°C',
        coords: { lat: 8.2280, lon: 124.2452 }
      },
      {
        name: 'Cagayan de Oro',
        region: 'Northern Mindanao',
        severity: typhoonData ? 'HIGH' : 'LOW',
        warnings: typhoonData ? [
          'Cagayan River flood alert',
          'Coastal areas evacuation ready',
          'Airport operations suspended'
        ] : [
          'River level monitoring',
          'Coastal advisories',
          'Normal operations'
        ],
        windSpeed: typhoonData ? '70-100 kph' : '20-35 kph',
        temperature: '25-30°C',
        coords: { lat: 8.4542, lon: 124.6319 }
      },
      {
        name: 'Butuan City',
        region: 'Caraga',
        severity: typhoonData ? 'HIGH' : 'MEDIUM',
        warnings: typhoonData ? [
          'Agusan River major flooding expected',
          'Low-lying areas total evacuation',
          'Transportation severely disrupted'
        ] : [
          'Agusan River elevated levels',
          'Wetland area monitoring',
          'Agricultural flood warnings'
        ],
        windSpeed: typhoonData ? '75-110 kph' : '30-50 kph',
        temperature: '26-31°C',
        coords: { lat: 8.9470, lon: 125.5406 }
      },
      {
        name: 'Davao City',
        region: 'Davao Region',
        severity: typhoonData ? 'MEDIUM' : 'LOW',
        warnings: typhoonData ? [
          'Davao River flood watch',
          'Coastal flooding possible',
          'Wind damage to structures'
        ] : [
          'Generally fair conditions',
          'Afternoon thunderstorms',
          'Monitor weather updates'
        ],
        windSpeed: typhoonData ? '60-85 kph' : '15-30 kph',
        temperature: '24-32°C',
        coords: { lat: 7.0731, lon: 125.6128 }
      },
      {
        name: 'General Santos',
        region: 'SOCCSKSARGEN',
        severity: typhoonData ? 'MEDIUM' : 'LOW',
        warnings: typhoonData ? [
          'Sarangani Bay rough seas',
          'Fishing operations suspended',
          'Port closures possible'
        ] : [
          'Normal weather conditions',
          'Light sea breeze',
          'Fishing operations normal'
        ],
        windSpeed: typhoonData ? '55-80 kph' : '20-35 kph',
        temperature: '25-33°C',
        coords: { lat: 6.1164, lon: 125.1716 }
      },
      {
        name: 'Zamboanga City',
        region: 'Zamboanga Peninsula',
        severity: typhoonData ? 'HIGH' : 'LOW',
        warnings: typhoonData ? [
          'Sulu Sea storm surge warning',
          'Port operations halted',
          'Coastal barangay evacuation'
        ] : [
          'Sea conditions normal',
          'Regular port operations',
          'Monitor weather advisories'
        ],
        windSpeed: typhoonData ? '80-115 kph' : '25-40 kph',
        temperature: '26-32°C',
        coords: { lat: 6.9214, lon: 122.0790 }
      }
    ];

    return cities;
  };

  const generateMindanaoFloodingWarnings = () => {
    return [
      {
        name: 'Iligan City',
        region: 'Northern Mindanao',
        riskLevel: typhoonData ? 'CRITICAL' : 'HIGH',
        waterLevel: typhoonData ? 'Mandulog River: 15.2m (Critical)' : 'Mandulog River: 12.8m (Alert)',
        warnings: typhoonData ? [
          'Forced evacuation: Tubod, Pala-o riverside',
          'All bridges closed to light vehicles',
          'Emergency shelters fully activated'
        ] : [
          'River level rising steadily',
          'Barangay evacuation preparations',
          'Monitor DRRMO announcements'
        ],
        lastUpdate: '3 minutes ago',
        trend: typhoonData ? 'rising rapidly' : 'stable',
        tributaries: ['Mandulog River', 'Dalipuga Creek', 'Saray Creek']
      },
      {
        name: 'Cagayan de Oro',
        region: 'Northern Mindanao',
        riskLevel: typhoonData ? 'HIGH' : 'MEDIUM',
        waterLevel: typhoonData ? 'Cagayan River: 8.5m (High)' : 'Cagayan River: 6.2m (Normal)',
        warnings: typhoonData ? [
          'Riverside communities on alert',
          'Carmen Bridge monitoring',
          'Upstream dam coordination active'
        ] : [
          'Normal river operations',
          'Regular monitoring active',
          'No immediate concerns'
        ],
        lastUpdate: '5 minutes ago',
        trend: typhoonData ? 'rising' : 'stable',
        tributaries: ['Cagayan River', 'Iponan River', 'Tagoloan River']
      },
      {
        name: 'Butuan City',
        region: 'Caraga',
        riskLevel: typhoonData ? 'CRITICAL' : 'HIGH',
        waterLevel: typhoonData ? 'Agusan River: Critical flood stage' : 'Agusan River: Elevated',
        warnings: typhoonData ? [
          'Massive wetland flooding ongoing',
          'Agricultural areas submerged',
          'Transportation severely affected'
        ] : [
          'Wetland areas flood-prone',
          'Agricultural monitoring',
          'Seasonal flood preparations'
        ],
        lastUpdate: '7 minutes ago',
        trend: typhoonData ? 'rising rapidly' : 'rising slowly',
        tributaries: ['Agusan River', 'Kahimunan River', 'Taguibo River']
      },
      {
        name: 'Koronadal City',
        region: 'SOCCSKSARGEN',
        riskLevel: typhoonData ? 'HIGH' : 'MEDIUM',
        waterLevel: typhoonData ? 'Marbel River: High alert' : 'Marbel River: Watch level',
        warnings: typhoonData ? [
          'Agricultural valley flooding',
          'Rural road closures',
          'Livestock evacuation advised'
        ] : [
          'Agricultural area monitoring',
          'Irrigation system checks',
          'Farmer advisories issued'
        ],
        lastUpdate: '10 minutes ago',
        trend: 'rising',
        tributaries: ['Marbel River', 'Silway River', 'Banga Creek']
      },
      {
        name: 'Marawi City',
        region: 'BARMM',
        riskLevel: typhoonData ? 'HIGH' : 'LOW',
        waterLevel: typhoonData ? 'Lake Lanao: Storm surge risk' : 'Lake Lanao: Normal level',
        warnings: typhoonData ? [
          'Lake storm surge possible',
          'Lakeshore communities alert',
          'Reconstruction areas vulnerable'
        ] : [
          'Lake conditions stable',
          'Normal lakeshore activities',
          'Reconstruction monitoring'
        ],
        lastUpdate: '12 minutes ago',
        trend: typhoonData ? 'rising' : 'stable',
        tributaries: ['Lake Lanao', 'Agus River', 'Baloi Creek']
      },
      {
        name: 'Dipolog City',
        region: 'Zamboanga Peninsula',
        riskLevel: typhoonData ? 'HIGH' : 'MEDIUM',
        waterLevel: typhoonData ? 'Dipolog River: Flood stage' : 'Dipolog River: Elevated',
        warnings: typhoonData ? [
          'Coastal and river flooding',
          'Airport area inundation risk',
          'Port operations suspended'
        ] : [
          'River mouth monitoring',
          'Coastal advisory active',
          'Normal port operations'
        ],
        lastUpdate: '8 minutes ago',
        trend: 'rising',
        tributaries: ['Dipolog River', 'Sicayab River', 'Polanco Creek']
      }
    ];
  };

  const getSeverityColor = (severity) => {
    switch (severity) {
      case 'HIGH': return 'bg-red-500 text-white';
      case 'MEDIUM': return 'bg-orange-500 text-white';
      case 'LOW': return 'bg-yellow-500 text-white';
      default: return 'bg-gray-500 text-white';
    }
  };

  const getRiskColor = (risk) => {
    switch (risk) {
      case 'CRITICAL': return 'bg-red-600 text-white';
      case 'HIGH': return 'bg-red-500 text-white';
      case 'MEDIUM': return 'bg-orange-500 text-white';
      case 'LOW': return 'bg-yellow-500 text-white';
      default: return 'bg-gray-500 text-white';
    }
  };

  const getTrendIcon = (trend) => {
    switch (trend) {
      case 'rising rapidly': return '🔴↗️';
      case 'rising': return '🟡↗️';
      case 'falling': return '🟢↘️';
      case 'stable': return '🔵➡️';
      default: return '⚪—';
    }
  };

  const severeWeatherCities = generateMindanaoSevereWeatherAlerts();
  const floodingCities = generateMindanaoFloodingWarnings();

  return (
    <div className="grid lg:grid-cols-2 gap-4 mb-6">
      {/* Mindanao Severe Weather Alerts */}
      <motion.div
        initial={{ opacity: 0, x: -20 }}
        animate={{ opacity: 1, x: 0 }}
        className="bg-white rounded-xl shadow-lg p-4 border border-red-100"
      >
        <div className="flex items-center mb-3">
          <div className="p-2 bg-red-100 rounded-lg mr-2">
            <SafeIcon icon={FiAlertTriangle} className="text-lg text-red-600" />
          </div>
          <h3 className="text-lg font-bold text-red-900">Mindanao Severe Weather Alerts</h3>
        </div>

        <div className="space-y-2">
          {severeWeatherCities.map((city, index) => (
            <motion.div
              key={city.name}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.05 * index }}
              className="p-3 bg-gray-50 rounded-lg border hover:shadow-md transition-all"
            >
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center">
                  <SafeIcon icon={FiMapPin} className="text-red-500 mr-1 text-sm" />
                  <h4 className="font-semibold text-sm text-gray-900">{city.name}</h4>
                  <span className="text-xs text-gray-600 ml-1">({city.region})</span>
                  {city.name === 'Iligan City' && (
                    <span className="ml-2 px-2 py-0.5 bg-blue-100 text-blue-700 rounded-full text-xs font-medium">
                      📍 LOCAL
                    </span>
                  )}
                </div>
                <span className={`px-2 py-1 rounded text-xs font-medium ${getSeverityColor(city.severity)}`}>
                  {city.severity}
                </span>
              </div>

              <div className="grid grid-cols-2 gap-2 mb-2 text-xs">
                <div className="flex items-center">
                  <SafeIcon icon={FiWind} className="mr-1 text-blue-500" />
                  <span>{city.windSpeed}</span>
                </div>
                <div className="flex items-center">
                  <SafeIcon icon={FiThermometer} className="mr-1 text-orange-500" />
                  <span>{city.temperature}</span>
                </div>
              </div>

              <div className="space-y-1">
                {city.warnings.slice(0, 2).map((warning, idx) => (
                  <div key={idx} className="flex items-start text-xs text-gray-700">
                    <span className="text-red-500 mr-1 mt-0.5">•</span>
                    <span className="line-clamp-1">{warning}</span>
                  </div>
                ))}
              </div>

              {/* Special Iligan-specific information */}
              {city.name === 'Iligan City' && (
                <div className="mt-2 pt-2 border-t border-gray-200">
                  <div className="text-xs text-blue-700 bg-blue-50 p-1 rounded">
                    <strong>Local Focus:</strong> Mandulog River system - 6 major barangays affected by flooding patterns
                  </div>
                </div>
              )}
            </motion.div>
          ))}
        </div>
      </motion.div>

      {/* Mindanao Flooding Warnings */}
      <motion.div
        initial={{ opacity: 0, x: 20 }}
        animate={{ opacity: 1, x: 0 }}
        className="bg-white rounded-xl shadow-lg p-4 border border-blue-100"
      >
        <div className="flex items-center mb-3">
          <div className="p-2 bg-blue-100 rounded-lg mr-2">
            <SafeIcon icon={FiDroplet} className="text-lg text-blue-600" />
          </div>
          <h3 className="text-lg font-bold text-blue-900">Mindanao Flooding Warnings</h3>
        </div>

        <div className="space-y-2">
          {floodingCities.map((city, index) => (
            <motion.div
              key={city.name}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.05 * index }}
              className="p-3 bg-gray-50 rounded-lg border hover:shadow-md transition-all"
            >
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center">
                  <SafeIcon icon={FiMapPin} className="text-blue-500 mr-1 text-sm" />
                  <h4 className="font-semibold text-sm text-gray-900">{city.name}</h4>
                  <span className="text-xs text-gray-600 ml-1">({city.region})</span>
                  {city.name === 'Iligan City' && (
                    <span className="ml-2 px-2 py-0.5 bg-green-100 text-green-700 rounded-full text-xs font-medium">
                      📍 HOME
                    </span>
                  )}
                </div>
                <span className={`px-2 py-1 rounded text-xs font-medium ${getRiskColor(city.riskLevel)}`}>
                  {city.riskLevel}
                </span>
              </div>

              <div className="flex items-center justify-between mb-2 text-xs">
                <div className="flex items-center text-blue-700 font-medium">
                  <span>{city.waterLevel}</span>
                  <span className="ml-1">{getTrendIcon(city.trend)}</span>
                </div>
                <span className="text-gray-500">{city.lastUpdate}</span>
              </div>

              <div className="space-y-1 mb-2">
                {city.warnings.slice(0, 2).map((warning, idx) => (
                  <div key={idx} className="flex items-start text-xs text-gray-700">
                    <span className="text-blue-500 mr-1 mt-0.5">•</span>
                    <span className="line-clamp-1">{warning}</span>
                  </div>
                ))}
              </div>

              {/* Tributaries information */}
              <div className="pt-2 border-t border-gray-100">
                <p className="text-xs text-gray-500 mb-1">Key Waterways:</p>
                <div className="flex flex-wrap gap-1">
                  {city.tributaries.map((tributary, idx) => (
                    <span key={idx} className="px-1.5 py-0.5 bg-blue-100 text-blue-700 rounded text-xs">
                      {tributary}
                    </span>
                  ))}
                </div>
              </div>

              {/* Special Iligan-specific flooding information */}
              {city.name === 'Iligan City' && (
                <div className="mt-2 pt-2 border-t border-gray-200">
                  <div className="text-xs text-green-700 bg-green-50 p-1 rounded">
                    <strong>Iligan Specific:</strong> Mandulog confluence with 3 major creeks - flash flood risk in 15 minutes during heavy rain
                  </div>
                </div>
              )}
            </motion.div>
          ))}
        </div>
      </motion.div>
    </div>
  );
};

export default MindanaoSevereWeather;